import { formatNumber } from 'i18n/format';
import { countLabel } from 'utils/ru';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import WavesIcon from '@mui/icons-material/Waves';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useTokens } from 'theme';
import { useWarehouseOverview, useWarehouseQuota } from 'api/warehouse';
import BarChart from 'components/common/BarChart';
import Panel from 'pages/console/bi-api/Panel';
import { StatCard } from 'pages/console/bi-api/StatCards';
import { fmtNumber, fmtRelative } from 'pages/console/bi-api/format';

import { useTranslation } from 'react-i18next';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

export function fmtBytes(bytes) {
  if (bytes === null || bytes === undefined) return '—';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${formatNumber(value, { maximumFractionDigits: value >= 10 || unit === 0 ? 0 : 1 })} ${BYTE_UNITS[unit]}`;
}

const COMPACT = { format: (value) => formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 }) };

const plural = (n, noun) => countLabel(n, noun);

function modelTotals(projects) {
  return (projects || []).reduce(
    (acc, p) => ({
      nodes: acc.nodes + p.nodes,
      built: acc.built + p.built,
      failed: acc.failed + p.failed,
      neverBuilt: acc.neverBuilt + p.never_built,
    }),
    { nodes: 0, built: 0, failed: 0, neverBuilt: 0 },
  );
}

export function LakehouseStats({ overview }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const { tables, freshness, loads, storage, direct_sql: sql } = overview;
  const models = modelTotals(tables.dbt_projects);

  let rawSub = t('data.noneLoadedYet');
  if (tables.raw) {
    rawSub = freshness.stale_raw_tables
      ? t('data.valueNotLoadedInValueh', { value1: fmtNumber(freshness.stale_raw_tables), value2: freshness.stale_after_hours })
      : t('data.allLoadedRecently');
  }
  let modelSub = t('data.noDbtProjectYet');
  if (models.nodes) {
    if (models.failed) modelSub = t('data.failedCount', { amount: fmtNumber(models.failed) });
    else if (models.neverBuilt) modelSub = t('data.valueNotBuiltYet', { value1: fmtNumber(models.neverBuilt) });
    else modelSub = t('data.allBuilt');
  }

  const cards = [
    {
      icon: <TableChartIcon fontSize="small" />,
      label: t('data.rawTables'),
      value: fmtNumber(tables.raw),
      sub: rawSub,
      accentColor: themeTokens.positive,
    },
    {
      icon: <AccountTreeIcon fontSize="small" />,
      label: t('data.modelsBuilt'),
      value: models.nodes ? `${fmtNumber(models.built)} / ${fmtNumber(models.nodes)}` : '—',
      sub: modelSub,
      accentColor: themeTokens.series[0],
    },
    {
      icon: <ScheduleIcon fontSize="small" />,
      label: t('data.lastLoad'),
      value: fmtRelative(freshness.last_raw_load_at),
      sub: t('data.modelsBuiltValue', { value1: fmtRelative(freshness.last_model_build_at) }),
      accentColor: themeTokens.info,
    },
    {
      icon: <TimelineIcon fontSize="small" />,
      label: t('data.rowsValued', { value1: loads.window_days }),
      value: COMPACT.format(loads.totals.rows),
      sub: t('data.valueOkValueFailed', { value1: plural(loads.totals.ok, 'pipeline'), value2: fmtNumber(loads.totals.failed) }),
      accentColor: themeTokens.series[2],
    },
    {
      icon: <StorageIcon fontSize="small" />,
      label: t('data.storage'),
      value: fmtBytes(storage.bytes),
      sub: storage.measured_at ? t('data.measuredValue', { value1: fmtRelative(storage.measured_at) }) : t('data.notMeasuredYet'),
      accentColor: themeTokens.warning,
    },
    {
      icon: <TerminalIcon fontSize="small" />,
      label: t('data.directSql'),
      value: sql.enabled ? t('data.on') : t('data.off'),
      sub: sql.enabled ? plural(sql.active_connections, 'activeConnection') : t('data.notEnabled'),
      accentColor: themeTokens.negative,
    },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid item xs={6} sm={4} lg={2} key={card.label}>
          <StatCard {...card} />
        </Grid>
      ))}
    </Grid>
  );
}

export function LoadsPanel({ loads }) {
  const { t } = useTranslation();
  return (
    <Panel icon={<TimelineIcon />} title={t('data.loadsValueDays', { value1: loads.window_days })}>
      <BarChart
        series={loads.series}
        total={(d) => d.ok + d.failed}
        part={(d) => d.failed}
        tooltip={(d) =>
          t('data.valueValueValueOkValueFailedValue', { value1: d.date, value2: plural(d.ok + d.failed, 'pipeline'), value3: d.ok, value4: d.failed, value5: plural(d.rows, 'row') })
        }
        emptyText={t('data.noLoadsInTheLastValueDays', { value1: loads.window_days })}
        totalLabel={t('data.succeeded')}
        partLabel={t('data.failed')}
      />
    </Panel>
  );
}

// Where to go next from the Lakehouse: direct SQL, model readiness, the plan.
export function LakehouseLinks({ overview }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const navigate = useNavigate();
  const { data: quota } = useWarehouseQuota();
  const project = overview.tables.dbt_projects[0];

  const row = (label, value, action) => (
    <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: themeTokens.inkMuted }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{value}</Typography>
      </Box>
      {action}
    </Box>
  );

  let planValue = '—';
  if (quota) {
    const byo = quota.limit === null ? t('data.unlimitedValue', { value: fmtNumber(quota.used) }) : `${quota.used} / ${quota.limit}`;
    planValue = t('data.valueOwnWarehousesValue', { value1: (quota.plan || '').toUpperCase(), value2: byo });
  }

  return (
    <Panel icon={<WavesIcon />} title={t('data.useYourData')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {row(
          t('data.directSql'),
          overview.direct_sql.enabled
            ? plural(overview.direct_sql.active_connections, 'activeConnection')
            : t('data.notEnabledForYourWorkspace'),
          <Button size="small" onClick={() => navigate('/console/connections')}>{t('data.connections')}</Button>,
        )}
        {row(
          t('data.modelReadiness'),
          project ? project.name : t('data.noDbtProjectYet2'),
          project && (
            <Button size="small" onClick={() => navigate(`/console/pipelines/preparation/${project.public_id}?tab=readiness`)}>{t('data.readiness')}</Button>
          ),
        )}
        {row(t('data.plan'), planValue, null)}
      </Box>
    </Panel>
  );
}

/** The Lakehouse on the Warehouse page: what it holds and how it is doing. */
export default function LakehouseCard({ warehouse }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: overview, isLoading, error } = useWarehouseOverview(warehouse.public_id);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WavesIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>{warehouse.name}</Typography>
          <Chip size="small" color="primary" label="Lakehouse" />
          {!warehouse.is_active && <Chip size="small" color="warning" label={t('data.disabled')} />}
        </Box>
        <Button size="small" variant="contained" onClick={() => navigate(`/console/pipelines/storage/${warehouse.public_id}`)}>{t('data.open')}</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.yourWorkspaceSOwnLakehouseManagedByThePlatformPipelinesLoad')}</Typography>
      {error && <Alert severity="error">{t('data.failedToLoadTheLakehouseOverview')}</Alert>}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      )}
      {overview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <LakehouseStats overview={overview} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}><LoadsPanel loads={overview.loads} /></Grid>
            <Grid item xs={12} md={4}><LakehouseLinks overview={overview} /></Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
}
